import type { AgentStatusResponse } from '../equanaut/services/api/agent/types'

export interface PrimeInfo {
  id: string
  userId: string
  status: string
  createdAt: string
}

export interface OrgEntry {
  orgId: string
  orgSlug: string
  userRole: 'guest' | 'employee' | 'owner' | 'creator'
  status: 'ok' | 'unreachable'
  lastKnownAt?: string
  agentCount?: number
  pendingHireRequests?: number
}

export interface CrossOrgSummary {
  orgs: OrgEntry[]
  totalOrgs: number
  unreachableOrgs: number
  assembledAt: string
}

function previewPrime(): { prime: PrimeInfo } {
  return {
    prime: {
      id: 'preview',
      userId: 'preview',
      status: 'preview',
      createdAt: new Date().toISOString(),
    },
  }
}

function emptyOrgSummary(): { summary: CrossOrgSummary } {
  return {
    summary: {
      orgs: [],
      totalOrgs: 0,
      unreachableOrgs: 0,
      assembledAt: new Date().toISOString(),
    },
  }
}

async function fetchAgentStatus(): Promise<AgentStatusResponse | null> {
  const res = await fetch('/api/v1/agent/status', { credentials: 'include' })
  if (!res.ok) return null
  return res.json()
}

async function fetchPrime(): Promise<{ prime: PrimeInfo }> {
  const res = await fetch('/api/v1/prime', { credentials: 'include' })
  if (res.status === 404) {
    return previewPrime()
  }
  if (!res.ok) throw new Error(`Prime fetch failed: ${res.status}`)
  return res.json()
}

async function fetchOrgSummary(): Promise<{ summary: CrossOrgSummary }> {
  const res = await fetch('/api/v1/prime/orgs', { credentials: 'include' })
  if (res.status === 404) {
    return emptyOrgSummary()
  }
  if (!res.ok) throw new Error(`Org summary fetch failed: ${res.status}`)
  return res.json()
}

export async function fetchPrimeBundle(): Promise<[{ prime: PrimeInfo }, { summary: CrossOrgSummary }]> {
  const agentStatus = await fetchAgentStatus().catch(() => null)
  if (agentStatus && (!agentStatus.enabled || agentStatus.agentEnabled === false)) {
    return [previewPrime(), await fetchOrgSummary().catch(() => emptyOrgSummary())]
  }

  const primeData = await fetchPrime()
  const summaryData = primeData.prime.id === 'preview' ? emptyOrgSummary() : await fetchOrgSummary()
  return [primeData, summaryData]
}
